import { motion } from 'framer-motion';
import { Badge } from './Badge';
import { BrandMark } from './BrandMark';
import { ArrowUpRightIcon, CarIcon, EffortIcon, FuelIcon, GaugeIcon, RadarIcon, SheetIcon, WalletIcon, WrenchIcon } from './icons';
import { SITUATION_LABEL, SITUATION_QUESTION } from '../utils/quickActions.js';
import './WelcomeScreen.css';

// Questions d'amorce d'un gestionnaire de flotte (UI-09) : un clic envoie la question
const SUGGESTIONS = [
  {
    id: 'parc',
    category: 'Parc',
    Icon: CarIcon,
    tone: 'violet',
    question: "Quel est l'état de disponibilité de mon parc aujourd'hui ?",
  },
  {
    id: 'maintenance',
    category: 'Maintenance',
    Icon: WrenchIcon,
    tone: 'orange',
    question: 'Quels véhicules ont une maintenance préventive en retard ?',
  },
  {
    id: 'conduite',
    category: 'Sécurité',
    Icon: GaugeIcon,
    tone: 'violet',
    question: 'Quels sont les 5 conducteurs avec le plus de survitesses ce mois-ci ?',
  },
  {
    id: 'couts',
    category: 'Coûts',
    Icon: WalletIcon,
    tone: 'orange',
    question: 'Compare les coûts de ma flotte de ce mois avec ceux du mois dernier.',
  },
  {
    id: 'carburant',
    category: 'Carburant',
    Icon: FuelIcon,
    tone: 'violet',
    question: 'Quels véhicules consomment plus de carburant que la normale ?',
  },
  {
    id: 'export',
    category: 'Rapports',
    Icon: SheetIcon,
    tone: 'orange',
    // MCP 90b55f7 : plus d'outil des équipements immobilisés (CEMELOG retiré) ; les
    // interventions en cours (get_intervention_en_cours) en sont l'alternative nommée
    question: 'Exporte en Excel la liste des interventions en cours.',
  },
];

const list = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.12 } },
};

const item = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.36, ease: [0, 0, 0, 1] } },
};

/**
 * Écran d'accueil : présentation, point de situation (sprint 6, CU-105) et suggestions.
 * `onSituation` : envoie la question du point de situation en analyse approfondie ;
 * `effortSupported` faux (modèle à profondeur fixe) : le badge « Analyse approfondie » est masqué.
 */
export function WelcomeScreen({ onSuggestion, onSituation = null, effortSupported = true, disabled = false }) {
  return (
    <section className="welcome" aria-labelledby="welcome-title">
      <motion.div
        className="welcome-hero"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0, 0, 0, 1] }}
      >
        <span className="welcome-mark" aria-hidden="true">
          <BrandMark size={50} />
        </span>
        <p className="welcome-eyebrow">Assistant digiTRANS</p>
        <h1 id="welcome-title" className="welcome-title">
          Que voulez-vous savoir sur <span className="welcome-title__accent">votre flotte</span>{'\u00a0'}?
        </h1>
        <p className="welcome-description">
          Posez votre question en langage naturel : l'assistant interroge vos données de flotte,
          les interprète et vous propose des actions chiffrées.
        </p>
      </motion.div>

      {onSituation && (
        <motion.div
          className="welcome-situation-wrap"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.36, delay: 0.08, ease: [0, 0, 0, 1] }}
        >
          <button
            type="button"
            className="situation-card"
            onClick={onSituation}
            disabled={disabled}
          >
            <span className="situation-card__icon" aria-hidden="true">
              <RadarIcon size={20} />
            </span>
            <span className="situation-card__body">
              <span className="situation-card__title">{SITUATION_LABEL}</span>
              <span className="situation-card__text">{SITUATION_QUESTION}</span>
            </span>
            {/* Modèle à profondeur fixe : aucune promesse d'analyse approfondie */}
            {effortSupported && (
              <Badge tone="prim" className="situation-card__badge">
                <EffortIcon size={12} level={3} />
                Analyse approfondie
              </Badge>
            )}
            <ArrowUpRightIcon size={16} className="suggestion-arrow situation-card__arrow" />
          </button>
        </motion.div>
      )}

      <motion.ul
        className="welcome-suggestions"
        aria-label="Suggestions de questions"
        variants={list}
        initial="hidden"
        animate="show"
      >
        {SUGGESTIONS.map(({ id, category, Icon, tone, question }) => (
          <motion.li key={id} variants={item}>
            <button
              type="button"
              className={`suggestion-card suggestion-card--${tone}`}
              onClick={() => onSuggestion?.(question)}
              disabled={disabled || !onSuggestion}
            >
              <span className="suggestion-icon" aria-hidden="true">
                <Icon size={18} />
              </span>
              <span className="suggestion-body">
                <span className="suggestion-category">{category}</span>
                <span className="suggestion-text">{question}</span>
              </span>
              <ArrowUpRightIcon size={16} className="suggestion-arrow" />
            </button>
          </motion.li>
        ))}
      </motion.ul>

      <p className="welcome-footnote">
        Les réponses s'appuient sur les données de votre plateforme. Vérifiez les chiffres avant toute décision importante.
      </p>
    </section>
  );
}
